/**
 * Wholesale Quick Add functionality for Shopify product cards
 * Handles adding products to cart and switching between add button and quantity selector
 */

window.WholesaleQuickAdd = (function() {
  'use strict';

  // Track quantities for each product
  let productQuantities = {};

  function handleInitialAdd(event, variantId, productId, sectionId) {
    event.preventDefault();
    
    const button = event.target;
    const spinner = button.querySelector('.loading-overlay__spinner');
    
    // Show loading state
    button.disabled = true;
    if (spinner) spinner.classList.remove('hidden');
    
    // Initialize quantity if not exists
    if (!productQuantities[productId]) {
      productQuantities[productId] = 0;
    }
    
    // Add 1 to cart
    productQuantities[productId] = 1;
    
    // Add to cart via AJAX
    addToCart(variantId, 1, function(success) {
      // Hide loading state
      button.disabled = false;
      if (spinner) spinner.classList.add('hidden');
      
      if (success) {
        // Show quantity selector, hide add button
        showQuantitySelector(productId, sectionId);
      }
    });
  }

  function showQuantitySelector(productId, sectionId) {
    const container = document.getElementById(`QuickAdd-${productId}-${sectionId}`);
    if (!container) return;
    
    const initialButton = container.querySelector('.wholesale-add-initial');
    const quantitySelector = container.querySelector('.wholesale-quantity-selector');
    const quantityInput = container.querySelector(`#Quantity-${productId}-${sectionId}`);
    
    if (initialButton) initialButton.style.display = 'none';
    if (quantitySelector) quantitySelector.style.display = 'block';
    if (quantityInput) quantityInput.value = productQuantities[productId] || 1;
  }

  function changeQuantity(variantId, productId, sectionId, change) {
    const quantityInput = document.querySelector(`#Quantity-${productId}-${sectionId}`);
    if (!quantityInput) return;
    
    let currentQuantity = parseInt(quantityInput.value) || 0;
    let newQuantity = Math.max(0, currentQuantity + change);
    
    if (newQuantity === 0) {
      // Remove from cart completely and show add button again
      removeFromCart(variantId, function(success) {
        if (success) {
          showAddButton(productId, sectionId);
          productQuantities[productId] = 0;
        }
      });
    } else {
      // Update quantity
      updateCartQuantity(variantId, newQuantity, function(success) {
        if (success) {
          quantityInput.value = newQuantity;
          productQuantities[productId] = newQuantity;
        }
      });
    }
  }

  function updateQuantity(variantId, productId, sectionId, newQuantity) {
    newQuantity = parseInt(newQuantity) || 0;
    
    if (newQuantity === 0) {
      // Remove from cart
      removeFromCart(variantId, function(success) {
        if (success) {
          showAddButton(productId, sectionId);
          productQuantities[productId] = 0;
        }
      });
    } else {
      // Update quantity
      updateCartQuantity(variantId, newQuantity, function(success) {
        if (success) {
          productQuantities[productId] = newQuantity;
        }
      });
    }
  }

  function showAddButton(productId, sectionId) {
    const container = document.getElementById(`QuickAdd-${productId}-${sectionId}`);
    if (!container) return;
    
    const initialButton = container.querySelector('.wholesale-add-initial');
    const quantitySelector = container.querySelector('.wholesale-quantity-selector');
    
    if (initialButton) initialButton.style.display = 'block';
    if (quantitySelector) quantitySelector.style.display = 'none';
  }

  // AJAX functions for cart operations
  function addToCart(variantId, quantity, callback) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      console.log('Added to cart:', data);
      if (callback) callback(true, data);
      
      // Update cart drawer - use multiple approaches
      updateCartDrawer(true); // true indicates this is after adding to cart
      
      // Update cart count
      updateCartCount();
      
      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      if (callback) callback(false, error);
    });
  }

  function updateCartQuantity(variantId, quantity, callback) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      console.log('Updated cart quantity:', data);
      if (callback) callback(true, data);
      
      // Update cart drawer
      updateCartDrawer(false);
      
      // Update cart count
      updateCartCount();
      
      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      if (callback) callback(false, error);
    });
  }

  function removeFromCart(variantId, callback) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 0);

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      console.log('Removed from cart:', data);
      if (callback) callback(true, data);
      
      // Update cart drawer
      updateCartDrawer(false);
      
      // Update cart count
      updateCartCount();
      
      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(error => {
      console.error('Error removing from cart:', error);
      if (callback) callback(false, error);
    });
  }

  function updateCartDrawer(isAddToCart = false) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Method 1: Try the standard approach first
    // Add a small delay to ensure cart is updated on server
    setTimeout(() => {
      updateCartDrawerStandard(cartDrawer, isAddToCart);
    }, 100);
  }

  function updateCartDrawerStandard(cartDrawer, isAddToCart) {
    // Get all required data
    Promise.all([
      fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`),
      fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`),
      fetch(`${window.Shopify.routes.root}cart.js`)
    ])
    .then(([drawerResponse, iconResponse, cartResponse]) => {
      return Promise.all([
        drawerResponse.text(),
        iconResponse.text(),
        cartResponse.json()
      ]);
    })
    .then(([drawerHtml, iconHtml, cartData]) => {
      // Method 1: Try renderContents with proper format for CartDrawer class
      if (cartDrawer.renderContents) {
        try {
          // Remove is-empty class if cart now has items
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          if (drawerInner && cartData.item_count > 0) {
            drawerInner.classList.remove('is-empty');
            cartDrawer.classList.remove('is-empty');
          }
          
          const parsedState = {
            id: cartData.items.length > 0 ? cartData.items[0].product_id : null,
            sections: {
              'cart-drawer': drawerHtml,
              'cart-icon-bubble': iconHtml
            }
          };
          
          cartDrawer.renderContents(parsedState);
          return;
        } catch (error) {
          console.error('Error with renderContents:', error);
        }
      }

      // Method 2: Try direct innerHTML replacement using CartDrawer's getSectionInnerHTML method
      try {
        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner && cartData.item_count > 0) {
          drawerInner.classList.remove('is-empty');
          cartDrawer.classList.remove('is-empty');
        }
        
        // Update CartDrawer section
        const cartDrawerElement = document.querySelector('#CartDrawer');
        if (cartDrawerElement && cartDrawer.getSectionInnerHTML) {
          cartDrawerElement.innerHTML = cartDrawer.getSectionInnerHTML(drawerHtml, '#CartDrawer');
        }
        
        // Update cart icon bubble
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        if (cartIconBubble && cartDrawer.getSectionInnerHTML) {
          cartIconBubble.innerHTML = cartDrawer.getSectionInnerHTML(iconHtml, '.shopify-section');
        }
        
        // Re-attach event listeners
        setTimeout(() => {
          cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
          reinitializeCartDrawer(cartDrawer);
        }, 50);
        
        return;
      } catch (error) {
        console.error('Error with getSectionInnerHTML:', error);
      }

      // Method 3: Try to find and replace specific cart content using CartDrawer's structure
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(drawerHtml, 'text/html');
        
        // Update the main CartDrawer content
        const newCartDrawerContent = doc.querySelector('#CartDrawer .drawer__inner');
        const currentCartDrawerContent = cartDrawer.querySelector('#CartDrawer .drawer__inner');
        
        if (newCartDrawerContent && currentCartDrawerContent) {
          // Remove is-empty class if cart now has items
          if (cartData.item_count > 0) {
            newCartDrawerContent.classList.remove('is-empty');
            currentCartDrawerContent.classList.remove('is-empty');
            cartDrawer.classList.remove('is-empty');
          }
          
          currentCartDrawerContent.innerHTML = newCartDrawerContent.innerHTML;
          
          // Update cart icon bubble
          const iconDoc = parser.parseFromString(iconHtml, 'text/html');
          const newIconBubble = iconDoc.querySelector('#cart-icon-bubble');
          const currentIconBubble = document.querySelector('#cart-icon-bubble');
          
          if (newIconBubble && currentIconBubble) {
            currentIconBubble.innerHTML = newIconBubble.innerHTML;
          }
          
          // Re-attach event listeners
          setTimeout(() => {
            cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
            reinitializeCartDrawer(cartDrawer);
          }, 50);
          
          return;
        }
      } catch (error) {
        console.error('Error with selective replacement:', error);
      }

      // Method 4: Force update using CartDrawer's built-in methods and structure
      try {
        // Try to manually update the sections that CartDrawer expects
        const sectionsToRender = [
          {
            id: 'cart-drawer',
            selector: '#CartDrawer',
            html: drawerHtml
          },
          {
            id: 'cart-icon-bubble',
            selector: '#cart-icon-bubble',
            html: iconHtml
          }
        ];
        
        sectionsToRender.forEach(section => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (sectionElement && cartDrawer.getSectionInnerHTML) {
            sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(section.html, section.selector);
          }
        });
        
        // Remove is-empty class if cart now has items
        if (cartData.item_count > 0) {
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          if (drawerInner) {
            drawerInner.classList.remove('is-empty');
          }
          cartDrawer.classList.remove('is-empty');
        }
        
        // Re-attach event listeners
        setTimeout(() => {
          cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        }, 50);
        
        return;
      } catch (error) {
        console.error('Error with manual section update:', error);
      }
    })
    .catch(error => {
      console.error('Error updating cart drawer:', error);
      // Final fallback - dispatch events and hope something catches them
      updateCartDrawerFallback();
    });
  }

  function reinitializeCartDrawer(cartDrawer) {
    // Re-attach event listeners and initialize components
    const quantityInputs = cartDrawer.querySelectorAll('input[type="number"]');
    quantityInputs.forEach(input => {
      // Remove existing listeners to avoid duplicates
      input.removeEventListener('change', handleQuantityChange);
      input.addEventListener('change', handleQuantityChange);
    });

    // Initialize any custom elements within the drawer
    const customElements = cartDrawer.querySelectorAll('[is], [data-shopify]');
    customElements.forEach(element => {
      if (element.connectedCallback) {
        element.connectedCallback();
      }
    });

    // Dispatch initialization event
    const initEvent = new CustomEvent('cart-drawer:initialized', {
      detail: { cartDrawer: cartDrawer },
      bubbles: true
    });
    cartDrawer.dispatchEvent(initEvent);
  }

  function handleQuantityChange(event) {
    // Handle quantity changes within the cart drawer
    const input = event.target;
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value) || 0;
    
    if (variantId) {
      updateCartQuantity(variantId, quantity, function(success) {
        if (!success) {
          // Revert the input value if update failed
          input.value = input.dataset.previousValue || 1;
        }
      });
    }
  }

  function updateCartDrawerFallback() {
    // Get current cart data and dispatch multiple events
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Try multiple event patterns that different themes use
        const eventNames = [
          'cart:updated',
          'cart:refresh',
          'cart:change',
          'cart-drawer:refresh',
          'cart-drawer:updated',
          'cartDrawer:refresh',
          'theme:cart:update',
          'ajaxCart:updated'
        ];
        
        eventNames.forEach(eventName => {
          const event = new CustomEvent(eventName, {
            detail: { cart: cart },
            bubbles: true
          });
          document.dispatchEvent(event);
        });

        // Try to manually trigger cart drawer methods
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const methods = ['updateCart', 'refresh', 'load', 'update', 'render'];
          methods.forEach(methodName => {
            if (typeof cartDrawer[methodName] === 'function') {
              try {
                cartDrawer[methodName](cart);
              } catch (error) {
                console.log(`Method ${methodName} failed:`, error);
              }
            }
          });
        }
      })
      .catch(error => {
        console.error('Error in cart drawer fallback:', error);
      });
  }

  function updateCartCount() {
    // Get current cart to update count
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count elements
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
        
        // Update cart count bubbles
        const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
        cartCountBubbles.forEach(bubble => {
          bubble.textContent = cart.item_count;
          if (cart.item_count > 0) {
            bubble.style.display = 'flex';
          } else {
            bubble.style.display = 'none';
          }
        });
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  }

  // Initialize on page load
  function init() {
    // Get current cart state to initialize quantities
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Initialize quantities from current cart
        cart.items.forEach(item => {
          const productId = item.product_id;
          productQuantities[productId] = item.quantity;
          
          // Show quantity selector for items already in cart
          const containers = document.querySelectorAll(`[id^="QuickAdd-${productId}-"]`);
          containers.forEach(container => {
            const sectionId = container.id.split('-').pop();
            showQuantitySelector(productId, sectionId);
          });
        });
      })
      .catch(error => {
        console.error('Error initializing cart state:', error);
      });
  }

  // Public API
  return {
    handleInitialAdd: handleInitialAdd,
    changeQuantity: changeQuantity,
    updateQuantity: updateQuantity,
    init: init
  };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', WholesaleQuickAdd.init);
} else {
  WholesaleQuickAdd.init();
}